import { useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// These pages use scalar string parameters; arrays and automatic coercion are
// deliberately not part of this hook's API.
export default function useUrlState<
  State extends { [Key in keyof State]: string | undefined }
>(
  initialState: State,
  { navigateMode = 'push' }: { navigateMode?: 'push' | 'replace' } = {}
) {
  const history = useHistory();
  const location = useLocation();
  const defaults = useRef(initialState);

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const result = { ...defaults.current };
    for (const key of Object.keys(result) as (keyof State & string)[]) {
      // Use the first value for duplicate parameters, as URLSearchParams does.
      if (params.has(key)) result[key] = params.get(key) as State[typeof key];
    }
    return result;
  }, [location.search]);

  const setState = useCallback(
    (changes: Partial<State>) => {
      // Read the latest location so consecutive updates don't overwrite each other.
      const current = history.location;
      const params = new URLSearchParams(current.search);
      for (const key of Object.keys(changes) as (keyof State & string)[]) {
        const value = changes[key];
        if (value === undefined || value === '') params.delete(key);
        else params.set(key, value);
      }
      const search = params.toString();
      history[navigateMode]({ ...current, search: search ? `?${search}` : '' });
    },
    [history, navigateMode]
  );

  return [state, setState] as const;
}
